import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { Home as HomeIcon, Stethoscope, FileText, MessagesSquare, MessageCircle, House, BookOpen, LineChart, User, LogOut, Menu, X, GraduationCap, CalendarClock } from "lucide-react";
import { Wordmark } from "@/components/marketing/Wordmark";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/lib/api";
import { useSeo } from "@/lib/seo";

// Grouped, journey-first navigation. Underlying routes (diagnostic, report, blueprint-intake,
// profile, purchases, timeline) still exist — reached from the hub pages / My Account.
// Consulting links show only when the account's service records include the matching product.
const CONSULTING = [
  ["/portal/clarity", "My Clarity", Stethoscope, false, ["diagnostic", "guided"]],
  ["/portal/my-blueprint", "My Blueprint", FileText, false, ["blueprint", "guided"]],
  ["/portal/guided", "Guided Support", MessagesSquare, false, ["guided", "guided_standalone"]],
  ["/portal/inhome", "In-Home + Overnight", House, false, ["inhome_overnight"]],
];

const buildGroups = (owned, enrolled) => {
  const consulting = CONSULTING.filter((i) => i[4].some((k) => owned.has(k)));
  return [
    ["Academic Studies", [
      ["/portal", "Student Dashboard", HomeIcon, true],
      ["/portal/resources", "Learning Center", BookOpen],
      ...(enrolled ? [["/portal#upcoming-classes", "My Classes & Schedule", CalendarClock, false, null, "classes"]] : []),
      ["/portal/progress", "Sleep Observation Tracker", LineChart],
      ["/courses", "Course Catalog", GraduationCap],
    ]],
    ["Student Services", [["/portal/messages", "Messages", MessageCircle], ["/portal/account", "My Account", User]]],
    ...(consulting.length ? [["Private Consulting · Separate service", consulting]] : []),
  ];
};

function NavGroups({ onNavigate, groups }) {
  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm border-l-2 transition-colors ${isActive ? "border-[#BDA677] bg-[#BDA677]/[0.12] text-[#F5F2E9] font-medium" : "border-transparent text-[#C3CAD3] hover:text-[#F5F2E9] hover:bg-white/[0.05]"}`;
  return (
    <>
      {groups.map(([title, items]) => (
        <div key={title} className="mb-5">
          <div className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#BDA677]/80">{title}</div>
          <div className="space-y-0.5">
            {items.map(([to, label, Icon, end, , tid]) => (
              <NavLink key={to} to={to} end={end} onClick={() => { onNavigate && onNavigate(); if (to.includes("#")) setTimeout(() => document.getElementById(to.split("#")[1])?.scrollIntoView({ behavior: "smooth" }), 50); }} data-testid={`pnav-${tid || to.split("/").pop() || "home"}`} className={to.includes("#") ? () => linkCls({ isActive: false }) : linkCls}>
                <Icon size={17} /> {label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [drawer, setDrawer] = useState(false);
  const [owned, setOwned] = useState(new Set());
  const [enrolled, setEnrolled] = useState(false);
  const groups = buildGroups(owned, enrolled);

  useEffect(() => {
    http.get("/portal/overview").then((r) => setOwned(new Set((r.data?.services || []).map((s) => s.product_key)))).catch(() => {});
    http.get("/portal/courses/enrollments").then((r) => setEnrolled(r.data.length > 0)).catch(() => {});
  }, []);
  useSeo({ title: "Student Portal | Infant Sleep Consultant Near Me Education Center", noindex: true });

  const doLogout = async () => { await logout(); nav("/"); };

  // Centralize the pre-account "What happened last night?" questionnaire onto the client
  // account as soon as the (entitled) customer enters the portal. The backend persists it
  // only when Clarity is owned; we clear the browser copy only once it has actually saved.
  useEffect(() => {
    let raw;
    try { raw = localStorage.getItem("isc_timeline"); } catch (e) { return; }
    if (!raw) return;
    http.put("/portal/intake/timeline", { timeline: JSON.parse(raw) })
      .then((r) => { if (r?.data && !r.data.skipped) { try { localStorage.removeItem("isc_timeline"); } catch (e) {} } })
      .catch(() => {});
  }, []);

  return (
    <div className="portal-academic min-h-screen bg-[#F5F2E9] flex">
      {/* desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-[#172A3A] border-r border-[#BDA677]/20 fixed inset-y-0">
        <Link to="/" className="flex items-center px-5 h-20 border-b border-[#BDA677]/20" data-testid="portal-brand">
          <Wordmark />
        </Link>
        <div className="px-5 pt-5 pb-1 text-[11px] text-[#8F9AA8] tracking-wide">Student Portal</div>
        <nav className="flex-1 p-3 overflow-y-auto"><NavGroups groups={groups} /></nav>
        <div className="p-3 border-t border-[#BDA677]/20">
          <div className="px-3 py-2 text-xs text-[#8F9AA8] truncate">{user?.email}</div>
          <button onClick={doLogout} data-testid="portal-logout" className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-[#C3CAD3] hover:text-[#F5F2E9] hover:bg-white/[0.05] w-full transition-colors"><LogOut size={17} /> Sign out</button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-[#172A3A] border-b border-[#BDA677]/25 h-14 flex items-center justify-between px-4">
        <button onClick={() => setDrawer(true)} data-testid="mobile-menu-open" aria-label="Open student menu" className="flex items-center gap-2 text-[#F5F2E9]"><Menu size={20} /><span className="font-academic text-lg">Menu</span></button>
        <Link to="/portal" className="font-academic text-[#F5F2E9] text-base" data-testid="portal-brand-mobile">Student Portal</Link>
      </div>

      {/* mobile slide-out drawer */}
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawer(false)} data-testid="mobile-drawer-backdrop" />
          <div className="absolute inset-y-0 left-0 w-[82%] max-w-xs bg-[#172A3A] shadow-2xl flex flex-col animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between px-5 h-16 border-b border-[#BDA677]/20">
              <Wordmark />
              <button onClick={() => setDrawer(false)} data-testid="mobile-menu-close" aria-label="Close menu" className="text-[#C3CAD3] ml-2"><X size={20} /></button>
            </div>
            <nav className="flex-1 p-3 overflow-y-auto"><NavGroups groups={groups} onNavigate={() => setDrawer(false)} /></nav>
            <div className="p-3 border-t border-[#BDA677]/20">
              <div className="px-3 py-2 text-xs text-[#8F9AA8] truncate">{user?.email}</div>
              <button onClick={doLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-[#C3CAD3] hover:bg-white/[0.05] w-full"><LogOut size={17} /> Sign out</button>
            </div>
          </div>
        </div>
      )}

      <main className="portal-main flex-1 min-w-0 lg:ml-64 pt-14 lg:pt-0 min-h-screen">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
